import { matchProjectSubfolder } from '../config/folder-conventions';
import { stage1InspectorNotesText } from './stage1-estimator-notes';
import { isOperatorDocumentClassSource, resolveDocumentClassification } from '../utils/document-classification';
import { plainTextToNotesHtml } from '../utils/notes-markdown';
import { fingerprintSourceContent } from '../utils/source-provenance';
import { extractContactDetailsApi } from './stage1-rfq-extraction';
import { selectAutomaticContactExtractionUpdates } from './stage1-extraction-fill-policy';
import { withoutUnchangedHeaderSeeds } from './stage1-header-seeds';
import { detectRfqByContent } from '../utils/rfq-content-detector';
import { notesImportHeader } from '../utils/ai-document-label';
import { stage1TextDocumentRoute } from './stage1-import-completion';

type Form = Record<string, unknown>;
interface IdentitySource { id: string; name: string; content: string; fingerprint: string }
interface IdentityReceipt {
    version: 1;
    sourceFingerprint: string;
    sourceDocumentId: string;
    sourceDocumentName: string;
    appliedFields: Record<string, string>;
}
export interface PreparedStage1Identity {
    source: IdentitySource;
    extracted: Form;
    initialFormData: Form;
}

function text(value: unknown): string { return typeof value === 'string' ? value.trim() : ''; }
// Match known extraction sentinels, not ordinary RFQ prefixes such as
// [EXTERNAL], [Page 1], or [QR601670].
const EXTRACTION_PLACEHOLDER = /^\[(?:PDF\s+contains\b|Could not\s+extract\b|No\s+(?:extractable|text content extracted)\b|Document extraction failed\b|Attached but extraction failed\b)/i;
function receipt(form: Form): IdentityReceipt | null {
    const value = form.stage1IdentityExtraction as IdentityReceipt | undefined;
    return value?.version === 1 && typeof value.sourceFingerprint === 'string' ? value : null;
}

/** Select existing readable RFQ evidence; this does not classify or grant scope authority. */
export function selectStage1IdentitySource(documents: unknown[]): IdentitySource | null {
    const candidates = documents.flatMap((value, index) => {
        if (!value || typeof value !== 'object') return [];
        const doc = value as Form;
        // A revision reason (JOBS-UX-SPEC 3.5 G1) is the office's own note
        // about a later Rev, never the client's request: it must not become
        // the source of the requester, the site or the Quote Request number.
        if (doc.source === 'revision-reason') return [];
        const name = text(doc.name) || text(doc.originalName) || text(doc.fileName);
        const content = text(doc.content) || text(doc.text) || text(doc.extractedText);
        if (content.length < 30 || EXTRACTION_PLACEHOLDER.test(content) || doc.extractionFailed === true || doc.contentComplete === false) return [];
        const resolved = resolveDocumentClassification(doc);
        // An assessor report or outgoing quotation cannot become the requester.
        if (resolved.classification !== 'rfq' && resolved.classification !== 'other') return [];
        // CN2: an unclassified document qualifies only when its text reads as
        // the request. Its file name is never read.
        const request = detectRfqByContent(content);
        if (resolved.classification !== 'rfq' && !request.isRfq) return [];
        // A stored 'rfq' on text that reads as a report, a quote or the office's
        // own inspection request (a class set by a file name before CN2) is not
        // the requester's document. An operator's own class is kept.
        if (request.rejectedAs && !isOperatorDocumentClassSource(resolved.source)) return [];
        const titledRequest = request.kind === 'formal-form' && Boolean(request.title);
        // A parsed email (the format, not the file name) ranks below a request form.
        const isEmail = doc.isEmail === true || typeof (doc.metadata as Form | undefined)?.emailFrom === 'string';
        const score = (resolved.classification === 'rfq' ? 100 : 0) + (resolved.trustedCanonicalClass ? 100 : 0) + (!isEmail ? 50 : 0) + (titledRequest ? 20 : 0);
        const id = text(doc.id) || text(doc.blobId) || name;
        const fingerprint = fingerprintSourceContent(JSON.stringify({ id, name, content }));
        return [{ source: { id, name, content, fingerprint }, score, index }];
    });
    candidates.sort((a, b) => b.score - a.score || a.index - b.index);
    return candidates[0]?.source || null;
}

/** Both attended and queued Summary use the ordinary Stage 1 extraction API. */
export async function prepareStage1ProjectIdentity({
    formData, documents, apiKey, model, projectId, signal, readEvenWhenComplete = false,
}: {
    formData: Form; documents: unknown[]; apiKey: string; model: string;
    projectId: string | null; signal: AbortSignal; readEvenWhenComplete?: boolean;
}): Promise<PreparedStage1Identity | null> {
    // A client the email header seeded and nobody has touched is not a known client:
    // it must not stop the request's own identity being read (RFQ 903025).
    const known = withoutUnchangedHeaderSeeds(formData);
    const complete = text(known.address) && (text(known.clientName) || text(known.clientCompany)) && text(known.orderNumber);
    // A Stage 1 drop reads the request on import and fills only what is still
    // empty. A folder import (readEvenWhenComplete) never had that read, so it
    // reads the request even when the folder name gave the address, client and
    // order number: the same read fills the job description, scope objective
    // and site contact (Peter, 2026-10-23: the routes must behave the same).
    // The receipt below stops a second read of the same request.
    if (complete && !readEvenWhenComplete) return null;
    const source = selectStage1IdentitySource(documents);
    if (!source || receipt(formData)?.sourceFingerprint === source.fingerprint) return null;
    const extracted = await extractContactDetailsApi({
        apiKey, model, projectId, signal, content: source.content, fileName: source.name,
        setExtractingDetails: () => {}, setExtractionMessage: () => {}, throwOnError: true,
    });
    if (!extracted || typeof extracted !== 'object' || Array.isArray(extracted)) {
        throw new Error('RFQ identity extraction returned no usable details. Review the Stage 1 fields or retry.');
    }
    return { source, extracted, initialFormData: formData };
}

/**
 * Record that Stage 1's own import already read the identity from the request (SPEC 2026-10-21 V10, Slice C C11).
 *
 * The import's RFQ extraction (Stage1DataEntry handleDocsFiles, the parallel extractContactDetails call) and the summary
 * run's prepareStage1ProjectIdentity send the SAME request: the same extraction API and model over the same request text.
 * Only the summary path wrote the receipt, so whenever the address, the client or the order number was still empty after the
 * import, the summary paid for the request a second time and got the same answer. Given the documents the import read and the
 * text it extracted from, this writes the receipt for the source the summary would pick, when that source's text is exactly
 * what the import sent. The summary path then finds the receipt and makes no call. A combined text of several documents is a
 * different read (the import's own note says combining changes the answer: several candidate order numbers, say), so it
 * leaves no receipt, and the summary still reads the request alone when a field stays empty. The fields the import filled
 * are its own (the import's fill policy), so the receipt records none. Returns the SAME object when there is nothing to
 * record, so callers can skip the write.
 */
export function recordImportIdentityExtraction(current: Form, documents: unknown[], extractedContent: unknown): Form {
    const source = selectStage1IdentitySource(Array.isArray(documents) ? documents : []);
    const sent = text(extractedContent);
    if (!source || !sent || sent !== source.content) return current;
    const priorReceipt = receipt(current);
    if (priorReceipt?.sourceFingerprint === source.fingerprint) return current;
    const nextReceipt: IdentityReceipt = {
        version: 1,
        sourceFingerprint: source.fingerprint,
        sourceDocumentId: source.id,
        sourceDocumentName: source.name,
        appliedFields: { ...(priorReceipt?.appliedFields || {}) },
    };
    return { ...current, stage1IdentityExtraction: nextReceipt };
}

/** Rebase after the provider await; any existing or newly edited human field wins. */
export function mergePreparedStage1Identity(current: Form, prepared: PreparedStage1Identity): Form {
    const initialUpdates = selectAutomaticContactExtractionUpdates(prepared.initialFormData, prepared.extracted);
    const liveUpdates = selectAutomaticContactExtractionUpdates(current, prepared.extracted);
    const priorReceipt = receipt(current);
    const updates = Object.fromEntries(Object.entries(liveUpdates).filter(([field]) => (
        Object.hasOwn(initialUpdates, field)
        && !(priorReceipt?.appliedFields?.[field] && priorReceipt.appliedFields[field] !== fingerprintSourceContent(text(current[field])))
    )));
    const nextReceipt: IdentityReceipt = {
        version: 1,
        sourceFingerprint: prepared.source.fingerprint,
        sourceDocumentId: prepared.source.id,
        sourceDocumentName: prepared.source.name,
        appliedFields: {
            ...(priorReceipt?.appliedFields || {}),
            ...Object.fromEntries(Object.entries(updates).map(([field, value]) => [field, fingerprintSourceContent(value)])),
        },
    };
    return { ...current, ...updates, stage1IdentityExtraction: nextReceipt };
}

// ─── Assessor site notes → Inspector Notes ───────────────────────────────────
//
// Operator report, 2026-09-20: importing one project at a time puts the
// assessor's notes document ("South Coogee.docx") into INSPECTOR NOTES; a
// queued import left the field holding only a video transcript. The attended
// Stage 1 screen did this itself with its own first-match writer (replaced on
// 2026-10-13 by a call to mergeStage1AssessorNotes below), and the queue never
// mounts that screen.
//
// It cannot select on the `assessor_notes` class. That class comes from the
// attended screen's AI batch classification; queue intake classifies
// deterministically and labels these files `other` (measured on six real
// projects). What does identify them is Customer Zero's own convention: a Word
// or text document inside the Site Documentation pack that is not the
// inspection REQUEST form, which is filed in both packs. Headings cannot be
// required either: Westmead's notes have none.

// Peter, 2026-10-13: "Make sure to get the docx reports into Inspector Notes in
// Stage 1. currently Site Assessor/Inspector reports only get added if they are
// uploaded as a txt file. We need to support more formats even picture files,
// if they are Site Inspection notes/report it should populate this in Stage 1".
// A file dropped on the import zone carries no relativePath, so the site-pack
// convention above never saw it, and every PDF or picture was refused by its
// extension. A document whose OPENING HEADING positively names a site
// inspection is now the inspector's write-up, in any readable format, with no
// folder or in the site pack (not in the client's RFQ pack or a later job
// folder). The site-pack convention stays as a second way in.
//
// Peter, 2026-10-13: "file names shouldnt dictate anything. only the
// contents." No rule here reads a file's NAME. A heading, a title line or the
// classifier's reading of the text decides, and the site pack (the operator's
// own folder layout) and the file's format are the only non-text inputs.

// content is what Inspector Notes receive; fingerprint identifies the file as
// written, so a receipt recorded before D6 review 6 still matches.
interface AssessorNotesSource { id: string; name: string; content: string; fingerprint: string }
interface AssessorNotesReceipt { version: 1; sources: Record<string, string> }

// The FORMAT of a file the site-pack convention accepts without a heading.
const ASSESSOR_NOTES_FORMAT = /\.(?:docx?|rtf|txt|md)$/i;
// A request form, read from the title lines of its text ("SDT Inspection
// Request", "Site Visit Request", "Quote Request").
const REQUEST_FORM_TITLE = /(?<![a-z])(?:(?:site\s+)?(?:inspection|visit|assessment)\s+request|quot(?:e|ation)\s+request|request\s+for\s+quot(?:e|ation)|work\s+order)(?![a-z])/i;
// The heading of the inspector's own write-up, without a bare "site visit":
// that phrase opens requests and letters ("site visit tomorrow at 10") as
// often as notes.
const SITE_INSPECTION_HEADING = /(?<![a-z])(?:site[\s_-]*(?:inspections?|assessments?|reports?|notes?|summary|documentation[\s_-]*reports?|visit[\s_-]*(?:reports?|notes?|summary))|inspection[\s_-]*(?:reports?|notes?)|assessment[\s_-]*(?:reports?|notes?)|(?:assessor|inspector)(?:'?s)?[\s_-]*notes?)(?![a-z])/i;
const OPENING_CHARS = 300;
// A third party's findings, not the site visitor's observations. A title line
// is specialist only when it also names a kind of document, so an assessor's
// line such as "Structural cracking to balcony" is not mistaken for an
// engineer's report.
const SPECIALIST_TERM = /(?<![a-z])(?:engineer|structural|asbestos|hazmat|hazardous|geotech|dilapidation|fire[\s_-]*safety|bca(?![a-z])|pest(?![a-z])|termite|acoustic|compliance[\s_-]*cert|certificate[\s_-]*of[\s_-]*compliance)/i;
const DOCUMENT_KIND = /report|assessment|survey|register|certificate|inspection|investigation|audit|statement|declaration|advice|review|summary|letter/i;
// A document already recognised as something else is never treated as notes.
// 'estimator_notes' (D6) carries the estimator's prices: it reaches the plan
// as its own price-stripped block, never as Inspector Notes.
const NOT_NOTES_CLASSES = new Set(['rfq', 'report', 'our_past_quote', 'competitor_quote', 'estimator_notes', 'plan', 'scope_of_works']);
// Two exceptions, both read from the text. 'report' covers the office's own
// site reports too (CN3 reads reports from their text), so a 'report' whose
// heading names a site inspection is the inspector's. And the classifier is inconsistent about formal site reports
// (measured on eleven real assessor documents: the same kind of file was
// 'other' on three projects and 'rfq' on two, because a site report quotes
// the request it answers), so an 'rfq' whose heading names a site inspection
// is the inspector's too, when it sits in the site pack or another document
// of the job is the RFQ.

function inSiteDocumentationPack(doc: Form): boolean {
    const path = text(doc.relativePath) || text(doc.sourceRelativePath);
    const segments = path.split(/[\/]+/).filter(Boolean);
    // The file itself is the last segment; any folder above it may be the pack.
    return segments.slice(0, -1).some(segment => matchProjectSubfolder(segment)?.role === 'site-docs');
}

// Filed in a numbered project folder other than the site pack: the client's
// RFQ pack, the quote, work order, contract, compliance, final inspections or
// defects. A report there is the client's, or belongs to a later stage of the
// job, never this quote's site visit. A file with no folder is not excluded.
function filedOutsideSitePack(doc: Form): boolean {
    const path = text(doc.relativePath) || text(doc.sourceRelativePath);
    const segments = path.split(/[\/]+/).filter(Boolean);
    return segments.slice(0, -1).some(segment => {
        const role = matchProjectSubfolder(segment)?.role;
        return !!role && role !== 'site-docs';
    });
}

// Short lines at the top of the text that read as titles, not sentences. PDF
// text often runs the title into the first sentence on one line ("SITE
// DOCUMENTATION REPORT. Access via the rear lane..."), so the text before the
// first full stop counts as a title when it is short.
function openingTitleLines(content: string): string[] {
    const opening = content.slice(0, OPENING_CHARS);
    const lines = opening.split(/\r?\n/).map(line => line.trim());
    const firstSentenceHead = ((lines.find(Boolean) || '').split(/[.?!](?:\s|$)/)[0] || '').trim();
    return [...lines, firstSentenceHead]
        .filter(line => line.length >= 3 && line.length <= 90 && !/[.?!,;]$/.test(line));
}

function specialistTitle(value: string): boolean {
    return SPECIALIST_TERM.test(value) && DOCUMENT_KIND.test(value);
}

/** A specialist's title line at the top of the text (see SPECIALIST_TERM). */
function specialistOpening(content: string): boolean {
    const firstLine = content.split(/\r?\n/).map(line => line.trim()).find(Boolean) || '';
    return specialistTitle(firstLine.slice(0, 120)) || openingTitleLines(content).some(specialistTitle);
}

/**
 * A request form by its own opening, whatever the file is called. PDF text
 * often has no line breaks, so the first 120 characters count as well as the
 * title lines.
 */
function requestFormOpening(content: string): boolean {
    return openingTitleLines(content).some(line => REQUEST_FORM_TITLE.test(line))
        || REQUEST_FORM_TITLE.test(content.slice(0, 120));
}

/**
 * Whether the opening heading of the text positively names a site inspection,
 * and nothing in it marks a specialist's report. An email is correspondence:
 * its subject names the report it forwards, not the text it carries.
 */
function headedAsSiteInspection(doc: Form, name: string, content: string): boolean {
    if (doc.isEmail === true || /\.(?:eml|msg)$/i.test(name)) return false;
    if (filedOutsideSitePack(doc)) return false;
    const heading = openingTitleLines(content).find(line => SITE_INSPECTION_HEADING.test(line));
    return !!heading && !specialistOpening(content);
}

/**
 * The inspector's own site notes or report, in document order. Deterministic:
 * no AI call, and no rule reads a file's name. A document qualifies when its
 * text is readable (any format: Word, PDF, a picture whose text was read,
 * plain text) and it is
 *   - classified assessor_notes, or
 *   - headed as a site inspection (see headedAsSiteInspection), unless its
 *     text is a request form or it is classified as something other than a
 *     report (with the 'rfq' exception in NOT_NOTES_CLASSES's note), or
 *   - a Word or text file inside the Site Documentation pack (the 2026-09-20
 *     convention, for notes with no heading), unless its text is a request
 *     form or a specialist's report.
 */
export function selectStage1AssessorNotes(documents: unknown[]): AssessorNotesSource[] {
    const docs = documents.filter((value): value is Form => !!value && typeof value === 'object');
    return docs.flatMap((doc) => {
        const name = text(doc.name) || text(doc.originalName) || text(doc.fileName);
        const content = text(doc.content) || text(doc.text) || text(doc.extractedText);
        if (content.length < 30 || EXTRACTION_PLACEHOLDER.test(content) || doc.extractionFailed === true) return [];
        const classification = String(resolveDocumentClassification(doc).classification);
        if (classification !== 'assessor_notes') {
            if (requestFormOpening(content)) return [];
            const sitePack = inSiteDocumentationPack(doc);
            const headed = headedAsSiteInspection(doc, name, content);
            let accepted: boolean;
            if (classification === 'report') {
                accepted = headed;
            } else if (classification === 'rfq') {
                const anotherRfq = docs.some(other => other !== doc
                    && String(resolveDocumentClassification(other).classification) === 'rfq');
                accepted = headed && (sitePack || anotherRfq);
            } else if (NOT_NOTES_CLASSES.has(classification)) {
                accepted = false;
            } else {
                accepted = headed || (sitePack && ASSESSOR_NOTES_FORMAT.test(name) && !specialistOpening(content));
            }
            if (!accepted) return [];
        }
        const id = text(doc.id) || text(doc.blobId) || name;
        // D6 review 6, CN1: text that reads as the builder's priced working
        // notes reaches Inspector Notes with its price lines removed, by the
        // same content rule as the Stage 1 imports, whatever the file is
        // called. Every other file is added as written.
        const notesText = text(stage1InspectorNotesText(content));
        return [{ id, name, content: notesText, fingerprint: fingerprintSourceContent(JSON.stringify({ name, content })) }];
    });
}

function assessorNotesReceipt(form: Form): AssessorNotesReceipt {
    const value = form.stage1AssessorNotes as AssessorNotesReceipt | undefined;
    return value?.version === 1 && value.sources && typeof value.sources === 'object'
        ? value
        : { version: 1, sources: {} };
}

// Letters and digits only. The notes hold HTML, and a list or bold line in
// the source turns into <li> or <strong> there, so punctuation, markup and
// entities are dropped from both sides before they are compared.
function plainWords(value: string): string {
    return value.replace(/<[^>]*>/g, ' ').replace(/&(?:[a-z]+|#\d+|#x[0-9a-f]+);/gi, ' ')
        .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * Append the assessor's notes to Inspector Notes. Append only: what a person
 * typed is never replaced or reordered. Returns the SAME object when there is
 * nothing to add, so callers can skip the save.
 *
 * Added once per source. A receipt records each document by content, so notes
 * the operator later deletes are not put back, and a one-at-a-time import that
 * already placed the same text (with or without a header) is not doubled. The
 * Stage 1 import and Summary both call this, and the same file can reach
 * Summary under another document id, so a recorded fingerprint counts
 * whichever id recorded it.
 */
export function mergeStage1AssessorNotes(current: Form, documents: unknown[]): Form {
    return appendStage1NotesSources(current, selectStage1AssessorNotes(documents));
}

/**
 * The plain text files a Stage 1 drop puts in Inspector Notes (its text step:
 * not a request or scope, not the business's estimator notes), read the same
 * way. A folder import (Import Projects, the Quote Run) kept them only as
 * documents (Peter, 2026-10-23: the routes must behave the same). They share
 * the assessor notes receipt, so each is added once and a deleted one stays out.
 */
export function selectStage1TextFileNotes(documents: unknown[]): AssessorNotesSource[] {
    return documents.filter((value): value is Form => !!value && typeof value === 'object')
        .filter(doc => stage1TextDocumentRoute(doc) === 'inspector_notes')
        .map((doc) => {
            const name = text(doc.name) || text(doc.originalName) || text(doc.fileName);
            const content = text(doc.content) || text(doc.text) || text(doc.extractedText);
            const id = text(doc.id) || text(doc.blobId) || name;
            return { id, name, content: text(stage1InspectorNotesText(content)), fingerprint: fingerprintSourceContent(JSON.stringify({ name, content })) };
        });
}

export function mergeStage1TextFileNotes(current: Form, documents: unknown[]): Form {
    return appendStage1NotesSources(current, selectStage1TextFileNotes(documents));
}

function appendStage1NotesSources(current: Form, selected: AssessorNotesSource[]): Form {
    const priorReceipt = assessorNotesReceipt(current);
    let notes = typeof current.notes === 'string' ? current.notes : '';
    const sources = { ...priorReceipt.sources };
    let changed = false;
    for (const source of selected) {
        if (sources[source.id] === source.fingerprint) continue;
        if (Object.values(sources).includes(source.fingerprint)) continue;
        const alreadyPresent = plainWords(notes).includes(plainWords(source.content).slice(0, 160));
        if (!alreadyPresent && source.content) {
            // CN5: the notes reach every later prompt, so the header names the
            // kind of file, never its name. The receipt keeps the document id.
            notes += plainTextToNotesHtml(`${notesImportHeader(source)}\n${source.content}`);
        }
        sources[source.id] = source.fingerprint;
        changed = true;
    }
    return changed ? { ...current, notes, stage1AssessorNotes: { version: 1, sources } } : current;
}
